import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import {
    SITE_SPACE_PATH_MAX_LENGTH,
    SITE_SPACE_PATH_MIN_LENGTH,
    SITE_TITLE_MAX_LENGTH,
    SITE_TITLE_MIN_LENGTH,
} from '@gitbook/api-client';
import {
    Description,
    DialogRoot,
    FieldError,
    Fieldset,
    Form,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { SpaceCreationDialog } from '../../Space';
import { PathOutput, PathPrefix } from './PathOutput';
import { SpacePickerField } from './SpacePickerField';
import { StickyFormControls } from './StickyFormControls';
import { useAvailableSiteSpaces } from './useAvailableSiteSpaces';

export interface EditSiteVariantFormProps {
    organization: api.Organization;
    site: api.Site;
    siteSpace: api.SiteSpace;
    siteStructure: api.SiteStructure;
    orgAllContent: api.OrganizationAllContent;
    disabled?: boolean;
}

type EditSiteVariantFormData = {
    title: string;
    path: string;
    spaceId: string;
};

function getDefaultValues(siteSpace: api.SiteSpace): EditSiteVariantFormData {
    return {
        title: siteSpace.title,
        path: siteSpace.path,
        spaceId: siteSpace.space.id,
    };
}

export function EditSiteVariantForm(props: EditSiteVariantFormProps) {
    const { organization, site, siteSpace, disabled, siteStructure, orgAllContent } = props;

    const spaces = useAvailableSiteSpaces({ siteStructure, orgAllContent });
    const createSpaceDialogTrigger = useDialogTriggerState();
    const form = useForm<EditSiteVariantFormData>({
        defaultValues: getDefaultValues(siteSpace),
    });
    const onSubmit: SubmitHandler<EditSiteVariantFormData> = async (data) => {
        await toast.catch(async () => {
            const updateBody = {
                ...(data.path && data.path !== siteSpace.path ? { path: data.path } : {}),
                ...(data.spaceId && data.spaceId !== siteSpace.space.id
                    ? { spaceId: data.spaceId }
                    : {}),
            };

            await Promise.all([
                Object.keys(updateBody).length > 0
                    ? apiClient.siteSpaces.updateSiteSpaceById(
                          organization.id,
                          site.id,
                          siteSpace.id,
                          updateBody
                      )
                    : null,
                data.title
                    ? apiClient.siteCustomization.overrideSiteSpaceCustomizationById(
                          organization.id,
                          site.id,
                          siteSpace.id,
                          { title: data.title }
                      )
                    : null,
            ]);
            toast.success('Variant updated');
            form.reset(data);
        }, 'Error while saving variant');
    };

    return (
        <>
            <DialogRoot {...createSpaceDialogTrigger.rootProps}>
                <SpaceCreationDialog
                    organizationId={organization.id}
                    target={{
                        type: 'siteVariant',
                        props: {
                            site,
                            siteSection: null,
                            onCreateSpace: (space) => {
                                form.setValue('spaceId', space.id, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                });
                            },
                        },
                    }}
                />
            </DialogRoot>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                <Fieldset className="p-4" disabled={disabled || form.formState.isSubmitting}>
                    <SpacePickerField
                        form={form}
                        name="spaceId"
                        spaces={[siteSpace.space, ...spaces]}
                        description="Select which space power this variant's content."
                        createSpaceDialogTrigger={createSpaceDialogTrigger}
                    />
                    <TextField
                        name="title"
                        required
                        minLength={SITE_TITLE_MIN_LENGTH}
                        maxLength={SITE_TITLE_MAX_LENGTH}
                    >
                        <Label>Title</Label>
                        <Description>Give this variant a clear label.</Description>
                        <TextInput autoFocus />
                        <FieldError />
                    </TextField>
                    <TextField
                        name="path"
                        required
                        minLength={SITE_SPACE_PATH_MIN_LENGTH}
                        maxLength={SITE_SPACE_PATH_MAX_LENGTH}
                    >
                        <Label>Slug</Label>
                        <Description>
                            Set the URL segment under which this variant lives.
                        </Description>
                        <TextInput
                            className="@container"
                            prefix={<PathPrefix item={siteSpace} />}
                        />
                        <PathOutput item={siteSpace} />
                        <FieldError />
                    </TextField>
                </Fieldset>
                <StickyFormControls>
                    <Submit className="self-end" label="Save" disabled={disabled} />
                </StickyFormControls>
            </Form>
        </>
    );
}
